import { useState } from 'react';
import { FileText, Plus, Edit, Trash2, Eye, EyeOff, Save, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BLOG_CATEGORIES = ['Industry', 'Education', 'Services', 'Sustainability', 'Operations', 'Markets', 'Technology'];

const INITIAL_FORM = {
  title: '', title_es: '', excerpt: '', excerpt_es: '',
  content: '', content_es: '', category: '', image_url: '',
  is_featured: false, is_published: true, read_time: '5 min'
};

const BlogManager = ({ posts, onRefresh, isSpanish }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);

  const resetForm = () => setForm(INITIAL_FORM);

  const openEdit = (post) => {
    setEditing(post);
    setForm({
      title: post.title || '',
      title_es: post.title_es || '',
      excerpt: post.excerpt || '',
      excerpt_es: post.excerpt_es || '',
      content: post.content || '',
      content_es: post.content_es || '',
      category: post.category || '',
      image_url: post.image_url || '',
      is_featured: post.is_featured || false,
      is_published: post.is_published !== false,
      read_time: post.read_time || '5 min'
    });
    setIsDialogOpen(true);
  };

  const openNew = () => {
    resetForm();
    setEditing(null);
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      const headers = { 'Content-Type': 'application/json' };
      const url = editing 
        ? `${API_URL}/api/admin/cms/blog/${editing._id}`
        : `${API_URL}/api/admin/cms/blog`;
      const method = editing ? 'PUT' : 'POST';
      
      const res = await fetch(url, { method, headers, body: JSON.stringify(form), credentials: 'include' });
      
      if (res.ok) {
        toast.success(isSpanish ? 'Artículo guardado' : 'Post saved successfully');
        setIsDialogOpen(false);
        setEditing(null);
        resetForm();
        onRefresh();
      } else {
        toast.error(isSpanish ? 'Error al guardar' : 'Failed to save');
      }
    } catch (error) {
      toast.error(isSpanish ? 'Error de conexión' : 'Connection error');
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm(isSpanish ? '¿Eliminar este artículo?' : 'Delete this post?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/cms/blog/${postId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (res.ok) {
        toast.success(isSpanish ? 'Artículo eliminado' : 'Post deleted');
        onRefresh();
      }
    } catch (error) {
      toast.error(isSpanish ? 'Error al eliminar' : 'Failed to delete');
    }
  };

  return (
    <>
      <Card className="border-0 shadow-lg">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>{isSpanish ? 'Artículos del Blog' : 'Blog Posts'}</CardTitle>
          <Button onClick={openNew} className="btn-accent">
            <Plus className="w-4 h-4 mr-2" />
            {isSpanish ? 'Nuevo Artículo' : 'New Post'}
          </Button>
        </CardHeader>
        <CardContent>
          {posts.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-steel-300 mx-auto mb-4" />
              <p className="text-steel-600">{isSpanish ? 'No hay artículos' : 'No posts yet'}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{isSpanish ? 'Título' : 'Title'}</TableHead>
                  <TableHead>{isSpanish ? 'Categoría' : 'Category'}</TableHead>
                  <TableHead>{isSpanish ? 'Estado' : 'Status'}</TableHead>
                  <TableHead>{isSpanish ? 'Fecha' : 'Date'}</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {posts.map(post => (
                  <TableRow key={post._id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {post.is_featured && (
                          <span className="px-2 py-0.5 bg-gold/20 text-gold text-xs rounded">Featured</span>
                        )}
                        <span className="font-medium">{post.title}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="bg-steel-100 px-2 py-1 rounded text-sm">{post.category}</span>
                    </TableCell>
                    <TableCell>
                      {post.is_published ? (
                        <span className="text-green-600 flex items-center gap-1"><Eye className="w-4 h-4" /> Published</span>
                      ) : (
                        <span className="text-steel-400 flex items-center gap-1"><EyeOff className="w-4 h-4" /> Draft</span>
                      )}
                    </TableCell>
                    <TableCell className="text-steel-500">
                      {post.created_at ? new Date(post.created_at).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(post)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(post._id)} className="text-red-500 hover:text-red-700">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Blog Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editing ? (isSpanish ? 'Editar Artículo' : 'Edit Post') : (isSpanish ? 'Nuevo Artículo' : 'New Post')}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-6 py-4">
            <div className="space-y-4">
              <div>
                <Label>{isSpanish ? 'Título (Inglés)' : 'Title (English)'}</Label>
                <Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} />
              </div>
              <div>
                <Label>{isSpanish ? 'Extracto (Inglés)' : 'Excerpt (English)'}</Label>
                <Textarea value={form.excerpt} onChange={(e) => setForm({...form, excerpt: e.target.value})} rows={2} />
              </div>
              <div>
                <Label>{isSpanish ? 'Contenido (Inglés)' : 'Content (English)'}</Label>
                <Textarea value={form.content} onChange={(e) => setForm({...form, content: e.target.value})} rows={6} />
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <Label>{isSpanish ? 'Título (Español)' : 'Title (Spanish)'}</Label>
                <Input value={form.title_es} onChange={(e) => setForm({...form, title_es: e.target.value})} />
              </div>
              <div>
                <Label>{isSpanish ? 'Extracto (Español)' : 'Excerpt (Spanish)'}</Label>
                <Textarea value={form.excerpt_es} onChange={(e) => setForm({...form, excerpt_es: e.target.value})} rows={2} />
              </div>
              <div>
                <Label>{isSpanish ? 'Contenido (Español)' : 'Content (Spanish)'}</Label>
                <Textarea value={form.content_es} onChange={(e) => setForm({...form, content_es: e.target.value})} rows={6} />
              </div>
            </div>
            <div className="col-span-2 grid grid-cols-3 gap-4">
              <div>
                <Label>{isSpanish ? 'Categoría' : 'Category'}</Label>
                <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                  <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    {BLOG_CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{isSpanish ? 'URL de Imagen' : 'Image URL'}</Label>
                <Input value={form.image_url} onChange={(e) => setForm({...form, image_url: e.target.value})} />
              </div>
              <div>
                <Label>{isSpanish ? 'Tiempo de Lectura' : 'Read Time'}</Label>
                <Input value={form.read_time} onChange={(e) => setForm({...form, read_time: e.target.value})} />
              </div>
            </div>
            <div className="col-span-2 flex gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.is_featured} onChange={(e) => setForm({...form, is_featured: e.target.checked})} className="rounded" />
                <span>{isSpanish ? 'Destacado' : 'Featured'}</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.is_published} onChange={(e) => setForm({...form, is_published: e.target.checked})} className="rounded" />
                <span>{isSpanish ? 'Publicado' : 'Published'}</span>
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              <X className="w-4 h-4 mr-2" /> {isSpanish ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button onClick={handleSave} className="btn-accent">
              <Save className="w-4 h-4 mr-2" /> {isSpanish ? 'Guardar' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BlogManager;
